'use client'

import { useEffect, useRef, useState } from 'react'

import { XIcon } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { toast } from 'sonner'

import { Upload } from '@/components/common'
import { Image } from '@/components/common/image'
import { useAppContext } from '@/components/providers/app-provider'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerTitle,
  DrawerTrigger,
} from '@/components/ui/drawer'
import { cn } from '@/lib/utils'

export type AssetUploadCategory = 'characters' | 'props' | 'scene'

export interface UploadAssetData {
  file: File | null
  url: string
  category: AssetUploadCategory
  name: string
  description: string
}

/** 与 AssetLibraryDialog 的 mode 保持一致；用本地字面量避免互相 import 造成循环依赖 */
export type UploadAssetMode = 'asset' | 'character'

export interface UploadAssetDialogProps {
  children: React.ReactNode
  mode?: UploadAssetMode
  /** 保存回调；可返回 Promise，UI 会在期间禁用保存按钮并显示 loading */
  onSave?: (data: UploadAssetData) => void | Promise<void>
  /** 是否在保存中（外部 controlled loading；不传则用内部 pending） */
  isSaving?: boolean
}

export const UploadAssetDialog = ({
  children,
  mode = 'asset',
  onSave,
  isSaving
}: UploadAssetDialogProps) => {
  const t = useTranslations('common')
  const { isMobile } = useAppContext()
  const [open, setOpen] = useState(false)
  const [file, setFile] = useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string>('')
  // sr-only 标题/描述按 mode 区分；既有 i18n key 直接复用
  const a11yTitle = t(mode === 'character' ? 'uploadCharacter' : 'uploadAsset')

  const handleUploadPick = (pickedFile: File) => {
    setFile(pickedFile)
    setPreviewUrl(URL.createObjectURL(pickedFile))
  }

  const handleClose = (nextOpen: boolean) => {
    setOpen(nextOpen)
    if (!nextOpen) {
      setFile(null)
      setPreviewUrl(prev => {
        if (prev) URL.revokeObjectURL(prev)
        return ''
      })
    }
  }

  const handleSave = async (data: UploadAssetData) => {
    // 等待外部 onSave 完成再关闭弹窗，避免上传/接口请求被中断
    await onSave?.(data)
    handleClose(false)
  }

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={handleClose}>
        <DrawerTrigger asChild>{children}</DrawerTrigger>
        <DrawerContent className="z-100 flex h-[95dvh] max-h-[95dvh] border-0 bg-transparent p-0 [&>div:first-child]:hidden">
          <DrawerTitle className="sr-only">{a11yTitle}</DrawerTitle>
          <DrawerDescription className="sr-only">{a11yTitle}</DrawerDescription>
          <UploadAssetContent
            file={file}
            isSaving={isSaving}
            mode={mode}
            previewUrl={previewUrl}
            onClose={() => handleClose(false)}
            onFileChange={handleUploadPick}
            onSave={handleSave}
          />
        </DrawerContent>
      </Drawer>
    )
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent
        className="z-100 flex h-[500px] w-[958px] max-w-[90vw] flex-col gap-0 overflow-hidden border-0 bg-transparent p-0"
        overlayClassName="bg-black/50"
        showCloseButton={false}
      >
        <DialogTitle className="sr-only">{a11yTitle}</DialogTitle>
        <DialogDescription className="sr-only">{a11yTitle}</DialogDescription>
        <UploadAssetContent
          file={file}
          isSaving={isSaving}
          mode={mode}
          previewUrl={previewUrl}
          onClose={() => handleClose(false)}
          onFileChange={handleUploadPick}
          onSave={handleSave}
        />
      </DialogContent>
    </Dialog>
  )
}

export interface UploadAssetInitialValues {
  previewUrl?: string
  category?: AssetUploadCategory | null
  name?: string
  description?: string
}

export interface UploadAssetContentProps {
  file: File | null
  previewUrl: string
  mode?: UploadAssetMode
  /** 保存中：禁用按钮并显示 loading 文案 */
  isSaving?: boolean
  onFileChange: (file: File) => void
  initialValues?: UploadAssetInitialValues
  onSave?: (data: UploadAssetData) => void | Promise<void>
  onClose?: () => void
}

export const UploadAssetContent = ({
  file,
  previewUrl,
  mode = 'asset',
  isSaving = false,
  onFileChange,
  initialValues,
  onSave,
  onClose
}: UploadAssetContentProps) => {
  const t = useTranslations('common')
  const { isMobile } = useAppContext()
  const isCharacterMode = mode === 'character'
  /**
   * 角色库模式下，Category 块隐藏并把 category 默认锁死为 'characters'，
   * 这样 canSave 仍可生效，上传产物也保证落到 characters 类目
   */
  const [category, setCategory] = useState<AssetUploadCategory | null>(() => {
    if (initialValues?.category !== undefined) return initialValues.category ?? null
    return isCharacterMode ? 'characters' : null
  })
  const [name, setName] = useState(() => initialValues?.name ?? '')
  const [description, setDescription] = useState(() => initialValues?.description ?? '')
  /** 移动端键盘弹出时表单覆盖上传区；键盘关闭后恢复上下布局（不依赖失焦） */
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false)

  const MAX_DESCRIPTION_LENGTH = 500
  const KEYBOARD_OPEN_THRESHOLD_PX = 120

  const categories: { value: AssetUploadCategory; labelKey: string; icon: React.ReactNode }[] = [
    { value: 'characters', labelKey: 'characters', icon: <CharactersIcon /> },
    { value: 'props', labelKey: 'props', icon: <PropsIcon /> },
    { value: 'scene', labelKey: 'scene', icon: <SceneIcon /> }
  ]

  useEffect(() => {
    if (!isMobile || typeof window === 'undefined') return

    const visualViewport = window.visualViewport
    if (!visualViewport) return

    const updateKeyboardState = () => {
      const keyboardHeight = window.innerHeight - visualViewport.height - visualViewport.offsetTop
      setIsKeyboardOpen(keyboardHeight > KEYBOARD_OPEN_THRESHOLD_PX)
    }

    const frameId = window.requestAnimationFrame(updateKeyboardState)
    visualViewport.addEventListener('resize', updateKeyboardState)
    visualViewport.addEventListener('scroll', updateKeyboardState)
    window.addEventListener('resize', updateKeyboardState)

    return () => {
      window.cancelAnimationFrame(frameId)
      visualViewport.removeEventListener('resize', updateKeyboardState)
      visualViewport.removeEventListener('scroll', updateKeyboardState)
      window.removeEventListener('resize', updateKeyboardState)
    }
  }, [isMobile])

  const trimmedName = name.trim()

  const handleSave = () => {
    if (!previewUrl || !category || !trimmedName || isSaving) return
    void onSave?.({
      file,
      url: previewUrl,
      category,
      name: trimmedName,
      description
    })
  }

  const canSave = Boolean(previewUrl) && Boolean(category) && Boolean(trimmedName) && !isSaving
  const isFormOverlay = isMobile && isKeyboardOpen

  return (
    <div
      style={{ boxShadow: '0px 8px 20px 0px rgba(0, 0, 0, 0.4)' }}
      className={cn(
        'relative flex overflow-hidden border border-[rgba(255,255,255,0.08)] bg-light-800',
        isMobile
          ? 'min-h-0 w-full flex-1 flex-col rounded-t-3xl'
          : 'size-full shrink-0 rounded-3xl'
      )}
    >
      {/* 图片预览 / 上传区：移动端置顶全宽；键盘弹出时被表单覆盖 */}
      <div
        className={cn(
          'relative flex shrink-0 items-center justify-center bg-[#00000029]',
          isMobile ? 'aspect-[16/9] w-full max-h-[24dvh] p-3' : 'aspect-square w-1/2 p-4',
          isFormOverlay && 'invisible'
        )}
      >
        {previewUrl ? (
          <Image
            alt="Preview"
            className="size-full rounded-lg object-cover"
            sizes="(max-width: 768px) 100vw, 480px"
            src={previewUrl}
          />
        ) : (
          <UploadDropZone onUpload={onFileChange} />
        )}
      </div>

      {/* 表单区：移动端不出现滚动条；键盘弹出时覆盖上传区，关闭键盘后恢复上下布局 */}
      <div
        className={cn(
          'flex flex-col',
          isMobile
            ? cn(
                'overflow-hidden px-4 pt-4 pb-[max(1rem,env(safe-area-inset-bottom))]',
                isFormOverlay
                  ? 'absolute inset-0 z-10 bg-light-800'
                  : 'min-h-0 flex-1'
              )
            : 'min-h-0 flex-1 p-6'
        )}
      >
        {/* 标题和关闭按钮 */}
        <div className={cn('flex shrink-0 items-center justify-between', isMobile ? 'mb-4' : 'mb-6')}>
          <h2 className="text-lg font-semibold text-[#dadada]">
            {isCharacterMode ? t('characters') : t('asset')}
          </h2>
          <button
            className="flex size-7 items-center justify-center rounded-md hover:bg-light/10"
            onClick={onClose}
          >
            <XIcon className="size-5 text-[#dadada]" />
          </button>
        </div>

        {/* Category（角色库模式下隐藏，category 已被默认锁死为 'characters'） */}
        {!isCharacterMode && (
          <div className="mb-4 shrink-0">
            <label className="mb-2 block text-sm text-[#dadada]">
              {t('category')} <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-2">
              {categories.map(cat => (
                <button
                  key={cat.value}
                  className={cn(
                    'flex flex-1 flex-col items-center justify-center gap-1.5 rounded-lg border py-3 transition-all',
                    category === cat.value
                      ? 'border-white/30 bg-white/10'
                      : 'border-[rgba(255,255,255,0.08)] hover:border-[rgba(255,255,255,0.2)]'
                  )}
                  onClick={() => setCategory(cat.value)}
                >
                  <span className="text-[#dadada]">{cat.icon}</span>
                  <span className="text-xs text-[#dadada]">{t(cat.labelKey)}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Name */}
        <div className="mb-4 shrink-0">
          <label className="mb-2 block text-sm text-[#dadada]">{t('name')}</label>
          <input
            className="w-full rounded-lg border border-[rgba(255,255,255,0.08)] bg-[#00000029] px-4 py-3 text-sm text-[#dadada] placeholder:text-[#7f7f7f] focus:border-[rgba(255,255,255,0.2)] focus:outline-none"
            placeholder={t(isCharacterMode ? 'characterName' : 'assetName')}
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
          />
        </div>

        {/* Description */}
        <div
          className={cn(
            'mb-6 flex min-h-0 flex-1 flex-col',
            isMobile && !isFormOverlay && 'min-h-28'
          )}
        >
          <label className="mb-2 block shrink-0 text-sm text-[#dadada]">{t('description')}</label>
          <div className="relative min-h-0 flex-1">
            <textarea
              className="size-full resize-none overflow-hidden rounded-lg border border-[rgba(255,255,255,0.08)] bg-[#00000029] px-4 py-3 pr-16 text-sm leading-[22px] text-[#dadada] placeholder:text-[#7f7f7f] focus:border-[rgba(255,255,255,0.2)] focus:outline-none"
              placeholder={t('descriptionPlaceholder')}
              value={description}
              onChange={e => setDescription(e.target.value.slice(0, MAX_DESCRIPTION_LENGTH))}
            />
            <span className="absolute right-3 bottom-3 text-xs text-[#7f7f7f]">
              {description.length} / {MAX_DESCRIPTION_LENGTH}
            </span>
          </div>
        </div>

        {/* Save Button */}
        <button
          disabled={!canSave}
          className={cn(
            'w-full shrink-0 rounded-[6px] py-3 text-sm font-medium transition-all',
            canSave
              ? 'bg-white text-[rgba(0,0,0,0.9)] hover:bg-white/90'
              : 'cursor-not-allowed bg-[rgba(255,255,255,0.32)] text-[#dadada] backdrop-blur-[12px]'
          )}
          onClick={handleSave}
        >
          {isSaving ? t('saving') : t('save')}
        </button>
      </div>
    </div>
  )
}

// ==================== Upload Drop Zone ====================

const UPLOAD_ACCEPT_TYPES = ['image/png', 'image/jpeg', 'image/webp']
const UPLOAD_ACCEPT_ATTR = 'image/png,image/jpeg,image/jpg,image/webp'
const MAX_UPLOAD_SIZE_BYTES = 5 * 1024 * 1024
const UPLOAD_FORMAT_LABEL = 'PNG, JPG, WEBP, ≤5MB'

interface UploadDropZoneProps {
  onUpload: (file: File) => void
}

const UploadDropZone = ({ onUpload }: UploadDropZoneProps) => {
  const t = useTranslations('common')
  const [isDragging, setIsDragging] = useState(false)
  const dragCounterRef = useRef(0)

  const validateAndUpload = (file: File | undefined) => {
    if (!file) return
    if (!UPLOAD_ACCEPT_TYPES.includes(file.type)) {
      toast.error(t('uploadInvalidFormat'))
      return
    }
    if (file.size > MAX_UPLOAD_SIZE_BYTES) {
      toast.error(t('uploadFileTooLarge'))
      return
    }
    onUpload(file)
  }

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    if (!e.dataTransfer.types.includes('Files')) return
    dragCounterRef.current += 1
    setIsDragging(true)
  }

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    dragCounterRef.current -= 1
    if (dragCounterRef.current <= 0) {
      dragCounterRef.current = 0
      setIsDragging(false)
    }
  }

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.dataTransfer) {
      e.dataTransfer.dropEffect = 'copy'
    }
  }

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    dragCounterRef.current = 0
    setIsDragging(false)
    const file = e.dataTransfer.files?.[0]
    validateAndUpload(file)
  }

  return (
    <div
      className={cn(
        'flex size-full flex-col items-center justify-center gap-3 rounded-lg transition-colors',
        isDragging && 'bg-white/5 ring-1 ring-white/20'
      )}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      <p className="px-6 text-center text-sm font-semibold tracking-[0.04em] text-[#dadada] uppercase">
        {t('uploadImageOrDragDrop')}
      </p>
      <p className="text-center text-xs tracking-[0.04em] text-[#7f7f7f] uppercase">
        {UPLOAD_FORMAT_LABEL}
      </p>
      <Upload
        asChild
        options={{ multiple: false, accept: UPLOAD_ACCEPT_ATTR }}
        onUpload={validateAndUpload}
      >
        <button
          className="mt-1 flex items-center gap-1.5 rounded-md border border-[rgba(255,255,255,0.16)] bg-transparent px-4 py-2 text-sm text-[#dadada] transition-colors hover:bg-white/5"
          type="button"
        >
          <UploadArrowIcon />
          <span>{t('upload')}</span>
        </button>
      </Upload>
    </div>
  )
}

// Icons
const CharactersIcon = () => (
  <svg className="size-5" fill="none" viewBox="0 0 20 20">
    <path
      d="M4.24244 15.8945C4.0461 16.2903 4.20779 16.7703 4.60359 16.9667C4.9994 17.163 5.47943 17.0013 5.67577 16.6055L4.95911 16.25L4.24244 15.8945ZM14.3206 16.6055C14.5169 17.0013 14.9969 17.163 15.3927 16.9667C15.7885 16.7703 15.9502 16.2903 15.7539 15.8945L15.0372 16.25L14.3206 16.6055ZM13.125 10H12.325C12.325 11.2841 11.2841 12.325 10 12.325V13.125V13.925C12.1677 13.925 13.925 12.1677 13.925 10H13.125ZM10 13.125V12.325C8.71594 12.325 7.675 11.2841 7.675 10H6.875H6.075C6.075 12.1677 7.83228 13.925 10 13.925V13.125ZM6.875 10H7.675C7.675 8.71594 8.71594 7.675 10 7.675V6.875V6.075C7.83228 6.075 6.075 7.83228 6.075 10H6.875ZM10 6.875V7.675C11.2841 7.675 12.325 8.71594 12.325 10H13.125H13.925C13.925 7.83228 12.1677 6.075 10 6.075V6.875ZM4.95911 16.25L5.67577 16.6055C6.07512 15.8005 6.69136 15.123 7.45505 14.6493L7.03339 13.9695L6.61173 13.2896C5.5948 13.9203 4.77422 14.8225 4.24244 15.8945L4.95911 16.25ZM7.03339 13.9695L7.45505 14.6493C8.21873 14.1757 9.09952 13.9247 9.99817 13.9247V13.1247V12.3247C8.80153 12.3247 7.62866 12.6589 6.61173 13.2896L7.03339 13.9695ZM9.99817 13.1247V13.9247C10.8968 13.9247 11.7776 14.1757 12.5413 14.6493L12.9629 13.9695L13.3846 13.2896C12.3677 12.6589 11.1948 12.3247 9.99817 12.3247V13.1247ZM12.9629 13.9695L12.5413 14.6493C13.305 15.123 13.9212 15.8005 14.3206 16.6055L15.0372 16.25L15.7539 15.8945C15.2221 14.8225 14.4015 13.9203 13.3846 13.2896L12.9629 13.9695ZM8 4V4.8H12V4V3.2H8V4ZM18 10H17.2V11H18H18.8V10H18ZM12 17V16.2H8V17V17.8H12V17ZM2 11H2.8V10H2H1.2V11H2ZM8 17V16.2C6.56317 16.2 5.56603 16.1983 4.81478 16.0973C4.08655 15.9994 3.71029 15.8216 3.44437 15.5556L2.87868 16.1213L2.31299 16.687C2.92575 17.2998 3.69593 17.5613 4.60159 17.683C5.48423 17.8017 6.6084 17.8 8 17.8V17ZM2 11H1.2C1.2 12.3916 1.1983 13.5158 1.31697 14.3984C1.43873 15.3041 1.70024 16.0743 2.31299 16.687L2.87868 16.1213L3.44437 15.5556C3.17844 15.2897 3.00061 14.9134 2.9027 14.1852C2.8017 13.434 2.8 12.4368 2.8 11H2ZM18 11H17.2C17.2 12.4368 17.1983 13.434 17.0973 14.1852C16.9994 14.9134 16.8216 15.2897 16.5556 15.5556L17.1213 16.1213L17.687 16.687C18.2998 16.0743 18.5613 15.3041 18.683 14.3984C18.8017 13.5158 18.8 12.3916 18.8 11H18ZM12 17V17.8C13.3916 17.8 14.5158 17.8017 15.3984 17.683C16.3041 17.5613 17.0743 17.2998 17.687 16.687L17.1213 16.1213L16.5556 15.5556C16.2897 15.8216 15.9134 15.9994 15.1852 16.0973C14.434 16.1983 13.4368 16.2 12 16.2V17ZM12 4V4.8C13.4368 4.8 14.434 4.8017 15.1852 4.9027C15.9134 5.00061 16.2897 5.17844 16.5556 5.44437L17.1213 4.87868L17.687 4.31299C17.0743 3.70024 16.3041 3.43873 15.3984 3.31697C14.5158 3.1983 13.3916 3.2 12 3.2V4ZM18 10H18.8C18.8 8.6084 18.8017 7.48423 18.683 6.60159C18.5613 5.69593 18.2998 4.92575 17.687 4.31299L17.1213 4.87868L16.5556 5.44437C16.8216 5.71029 16.9994 6.08655 17.0973 6.81478C17.1983 7.56603 17.2 8.56317 17.2 10H18ZM8 4V3.2C6.6084 3.2 5.48423 3.1983 4.60159 3.31697C3.69593 3.43873 2.92575 3.70024 2.31299 4.31299L2.87868 4.87868L3.44437 5.44437C3.71029 5.17844 4.08655 5.00061 4.81478 4.9027C5.56603 4.8017 6.56317 4.8 8 4.8V4ZM2 10H2.8C2.8 8.56317 2.8017 7.56603 2.9027 6.81478C3.00061 6.08655 3.17844 5.71029 3.44437 5.44437L2.87868 4.87868L2.31299 4.31299C1.70024 4.92575 1.43873 5.69593 1.31697 6.60159C1.1983 7.48423 1.2 8.6084 1.2 10H2Z"
      fill="currentColor"
    />
  </svg>
)

const PropsIcon = () => (
  <svg className="size-5" fill="none" viewBox="0 0 20 20">
    <path
      d="M9.98672 2.5C9.98321 4.48452 9.1933 6.38676 7.79003 7.79003C6.38676 9.1933 4.48452 9.98321 2.5 9.98672M17.5001 10.0134C15.5156 10.0169 13.6134 10.8068 12.2101 12.2101C10.8068 13.6134 10.0169 15.5156 10.0134 17.5001M17.5 10C17.5 14.1421 14.1421 17.5 10 17.5C5.85786 17.5 2.5 14.1421 2.5 10C2.5 5.85786 5.85786 2.5 10 2.5C14.1421 2.5 17.5 5.85786 17.5 10Z"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.6"
    />
  </svg>
)

const SceneIcon = () => (
  <svg className="size-5" fill="none" viewBox="0 0 20 20">
    <path
      d="M2.5 10H9.0625M1.875 15.625H18.125M3.4375 10V15.625M8.125 10V15.625M2.5 12.8125H9.0625M15 15.625V12.5M15 2.5L17.5 12.5H12.5L15 2.5ZM10.625 5.3125C10.625 6.17544 9.92544 6.875 9.0625 6.875C8.19956 6.875 7.5 6.17544 7.5 5.3125C7.5 4.44956 8.19956 3.75 9.0625 3.75C9.92544 3.75 10.625 4.44956 10.625 5.3125Z"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.6"
    />
  </svg>
)

const UploadArrowIcon = () => (
  <svg className="size-4" fill="none" viewBox="0 0 16 16">
    <path
      d="M2.7 13.5H13.3"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5"
    />
    <path
      d="M8 10.4V2.4"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5"
    />
    <path
      d="M4 6.4L8 2.4L12 6.4"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5"
    />
  </svg>
)

export default UploadAssetDialog
